import { z } from 'zod';
import { TimestampLike } from './timestamp-like';

// Statuses an inquiry may be in when a consultation is scheduled. Stored on the
// ConsultationRecord as `previousInquiryStatus` so cancellation can revert the
// inquiry. Shared by the scheduleConsultation callable's input gate (server-side
// validation) and the persisted record shape, so they cannot drift.
export const ConsultationPreviousInquiryStatusSchema = z.enum([
  'new',
  'contacted',
  'need_info',
  'consulting',
]);
export type ConsultationPreviousInquiryStatus = z.infer<
  typeof ConsultationPreviousInquiryStatusSchema
>;

// Outcomes a consultation can resolve to via the dispositionConsultation callable.
// Persisted on the ConsultationRecord as `outcome`. Shared with the callable
// contract so persisted shape and accepted input cannot drift.
export const ConsultationOutcomeSchema = z.enum(['need_info', 'not_a_fit', 'no_show']);
export type ConsultationOutcome = z.infer<typeof ConsultationOutcomeSchema>;

// Reasons a scheduled consultation can be cancelled via the cancelConsultation
// callable. Persisted on the ConsultationRecord as `cancelledReason`. Shared with
// the callable contract so persisted shape and accepted input cannot drift.
export const CancelledReasonSchema = z.enum([
  'prospect_cancelled',
  'attorney_unavailable',
  'conflict_discovered',
  'other',
]);
export type CancelledReason = z.infer<typeof CancelledReasonSchema>;

export const ConsultationRecordSchema = z.object({
  id: z.string(),
  scheduledAt: z.custom<TimestampLike>(),
  scheduledBy: z.string(),
  assignedAttorney: z.string().nullable(),
  createdAt: z.custom<TimestampLike>(),
  status: z.enum(['scheduled', 'completed', 'cancelled']),
  consultationType: z.enum(['phone', 'video', 'in_person']).nullable(),
  videoLink: z.string().nullable(),
  notes: z.string().nullable(),
  outcome: ConsultationOutcomeSchema.nullable(),
  outcomeNotes: z.string().nullable(),
  previousInquiryStatus: ConsultationPreviousInquiryStatusSchema,
  rescheduledFrom: z.custom<TimestampLike>().nullable(),
  cancelledAt: z.custom<TimestampLike>().nullable(),
  cancelledBy: z.string().nullable(),
  cancelledReason: CancelledReasonSchema.nullable(),
  cancelledNote: z.string().nullable(),
  revertedAt: z.custom<TimestampLike>().nullable(),
  revertedBy: z.string().nullable(),
  updatedAt: z.custom<TimestampLike>(),
  updatedBy: z.string(),
  calendarProvider: z.enum(['google', 'microsoft']).nullable(),
  calendarEventId: z.string().nullable(),
  calendarEventUrl: z.string().nullable(),
  durationMinutes: z.number().int().min(5).max(480).nullish().default(30),
});

export type ConsultationRecord = z.infer<typeof ConsultationRecordSchema>;

export const DenormalizedConsultationSchema = ConsultationRecordSchema.extend({
  firmId: z.string(),
  sessionId: z.string(),
  prospectName: z.string().nullish().default(null),
  prospectPhone: z.string().nullish().default(null),
  prospectEmail: z.string().nullish().default(null),
  practiceArea: z.string().nullish().default(null),
  assignedAttorneyName: z.string().nullish().default(null),
  conflictStatus: z.string().nullish().default(null),
});

export type DenormalizedConsultation = z.infer<typeof DenormalizedConsultationSchema>;
